import { create } from 'zustand';
import {
  clampVolume,
  defaultVolumes,
  type AudioChannel,
  type ChannelVolumes,
} from '../../engine/audio/channels';

export type Theme = 'dark' | 'light';

/**
 * Claves compartidas con el sitio público de Jekyll (`rs-web`).
 *
 * Se guardan como valores planos, no como JSON, porque `_layouts/default.html`
 * las lee tal cual antes de pintar. Cambiar el formato aquí haría que la
 * persona perdiera sus preferencias al pasar de rolsensorial.com a la
 * aplicación, así que no se usa el middleware `persist` de Zustand.
 */
const KEYS = {
  fontScale: 'rs-font-scale',
  theme: 'rs-theme',
  reduceMotion: 'rs-reduce-motion',
  muted: 'rs-audio-muted',
  volume: (channel: AudioChannel) => `rs-audio-${channel}`,
} as const;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // La preferencia vale para esta visita aunque el navegador impida guardarla.
  }
}

function readNumber(key: string, fallback: number): number {
  const parsed = Number(read(key));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * Tema inicial. Si no hay preferencia guardada se toma el que `index.html`
 * ya resolvió a partir de `prefers-color-scheme`, para no contradecir lo que
 * la persona está viendo.
 */
function initialTheme(): Theme {
  const stored = read(KEYS.theme);
  if (stored === 'light' || stored === 'dark') return stored;
  return document.documentElement.dataset.bsTheme === 'light' ? 'light' : 'dark';
}

export function initialVolumes(): ChannelVolumes {
  const volumes = { ...defaultVolumes };
  for (const channel of Object.keys(volumes) as AudioChannel[]) {
    const raw = read(KEYS.volume(channel));
    // `Number(null)` vale cero. Sin esta comprobación, un navegador que aún
    // no guardó preferencias iniciaba todos los canales completamente mudos.
    if (raw === null) continue;
    const stored = Number(raw);
    if (Number.isFinite(stored) && stored >= 0 && stored <= 1) volumes[channel] = stored;
  }
  return volumes;
}

export interface PreferencesState {
  fontScale: number;
  theme: Theme;
  reduceMotion: boolean;
  volumes: ChannelVolumes;
  muted: boolean;
  setFontScale(value: number): void;
  resetFontScale(): void;
  setTheme(theme: Theme): void;
  toggleTheme(): void;
  setReduceMotion(value: boolean): void;
  setVolume(channel: AudioChannel, value: number): void;
  setMuted(value: boolean): void;
}

/** Límites del escalado de texto declarados en `rules/marca.md`. */
export const FONT_SCALE_MIN = 0.85;
export const FONT_SCALE_MAX = 1.5;

export function clampFontScale(value: number): number {
  return Math.round(Math.min(FONT_SCALE_MAX, Math.max(FONT_SCALE_MIN, value)) * 100) / 100;
}

export const usePreferences = create<PreferencesState>((set, get) => ({
  fontScale: readNumber(KEYS.fontScale, 1),
  theme: initialTheme(),
  reduceMotion: read(KEYS.reduceMotion) === 'true',
  volumes: initialVolumes(),
  muted: read(KEYS.muted) === 'true',

  setFontScale(value) {
    const fontScale = clampFontScale(value);
    write(KEYS.fontScale, String(fontScale));
    set({ fontScale });
  },

  resetFontScale() {
    get().setFontScale(1);
  },

  setTheme(theme) {
    write(KEYS.theme, theme);
    set({ theme });
  },

  toggleTheme() {
    get().setTheme(get().theme === 'dark' ? 'light' : 'dark');
  },

  setReduceMotion(reduceMotion) {
    write(KEYS.reduceMotion, String(reduceMotion));
    set({ reduceMotion });
  },

  setVolume(channel, value) {
    const volume = clampVolume(value);
    write(KEYS.volume(channel), String(volume));
    set((state) => ({ volumes: { ...state.volumes, [channel]: volume } }));
  },

  setMuted(muted) {
    write(KEYS.muted, String(muted));
    set({ muted });
  },
}));
